/**
 * TTS Audio Cache using IndexedDB
 * Stores ElevenLabs generated audio to avoid regenerating
 */

const DB_NAME = 'vocario-tts-cache';
const DB_VERSION = 1;
const STORE_NAME = 'audio';

interface CachedAudio {
  key: string;
  blob: Blob;
  textLength: number;
  voiceId: string;
  createdAt: number;
  size: number;
}

// FNV-1a hash for cache key generation
function fnv1aHash(str: string): string {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16);
}

function getCacheKey(text: string, voiceId: string): string {
  return `${fnv1aHash(text + voiceId)}_${voiceId}`;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('createdAt', 'createdAt', { unique: false });
        store.createIndex('voiceId', 'voiceId', { unique: false });
      }
    };
  });
}

/**
 * Get cached TTS audio
 */
export async function getTTSFromCache(text: string, voiceId: string): Promise<Blob | null> {
  try {
    const db = await openDatabase();
    const key = getCacheKey(text, voiceId);

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result as CachedAudio | undefined;
        if (result) {
          console.log('🎵 TTS cache hit:', { key, size: result.size });
          resolve(result.blob);
        } else {
          resolve(null);
        }
      };
    });
  } catch (error) {
    console.error('Error reading TTS cache:', error);
    return null;
  }
}

/**
 * Save TTS audio to cache
 */
export async function saveTTSToCache(text: string, voiceId: string, blob: Blob): Promise<void> {
  try {
    const db = await openDatabase();
    const key = getCacheKey(text, voiceId);

    const cached: CachedAudio = {
      key,
      blob,
      textLength: text.length,
      voiceId,
      createdAt: Date.now(),
      size: blob.size,
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(cached);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        console.log('💾 TTS cached:', { key, size: blob.size });
        resolve();
      };
    });
  } catch (error) {
    console.error('Error saving to TTS cache:', error);
  }
}

/**
 * Clear all TTS cache
 */
export async function clearTTSCache(): Promise<void> {
  try {
    const db = await openDatabase();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        console.log('🗑️ TTS cache cleared');
        resolve();
      };
    });
  } catch (error) {
    console.error('Error clearing TTS cache:', error);
  }
}

/**
 * Get TTS cache statistics
 */
export async function getTTSCacheStats(): Promise<{ count: number; totalSize: number }> {
  try {
    const db = await openDatabase();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const items = request.result as CachedAudio[];
        const totalSize = items.reduce((sum, item) => sum + item.size, 0);
        resolve({ count: items.length, totalSize });
      };
    });
  } catch (error) {
    console.error('Error getting TTS cache stats:', error);
    return { count: 0, totalSize: 0 };
  }
}

/**
 * Format bytes to human readable string
 */
export function formatCacheSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}
